'use client';

import React, { useEffect, useState } from 'react';
import { cn } from '@/core/utils/cn';
import type { PriorityNavProps } from './PriorityNav';

type PriorityNavComponent = <T>(props: PriorityNavProps<T>) => React.ReactElement;

/**
 * Desktop menu row. Server-rendered (and on phones, where the row is display:none) it is a plain
 * single row that clips what doesn't fit — never wraps under the header. On a desktop-wide
 * screen it swaps in PriorityNav, which moves the overflow into "Więcej". PriorityNav is a
 * separate chunk loaded only there: the header bundle is on every storefront page, and even
 * ~1.5 KB more of it measurably delayed mobile LCP (zojahandmade, Lighthouse 89 → 85).
 */
export function DesktopNav<T>(props: PriorityNavProps<T>) {
    const [Enhanced, setEnhanced] = useState<PriorityNavComponent | null>(null);

    useEffect(() => {
        const mql = window.matchMedia('(min-width: 1024px)');
        let cancelled = false;
        const load = () => {
            import('./PriorityNav').then((m) => {
                if (!cancelled) setEnhanced(() => m.PriorityNav as PriorityNavComponent);
            });
        };
        if (mql.matches) {
            load();
            return () => { cancelled = true; };
        }
        const onChange = (e: MediaQueryListEvent) => {
            if (!e.matches) return;
            mql.removeEventListener('change', onChange);
            load();
        };
        mql.addEventListener('change', onChange);
        return () => { cancelled = true; mql.removeEventListener('change', onChange); };
    }, []);

    if (Enhanced) return <Enhanced {...props} />;

    // Same markup PriorityNav starts from (all items, one clipped row), so the swap doesn't move anything.
    return (
        <nav className={cn('relative flex flex-nowrap min-w-0 overflow-hidden', props.className)}>
            {props.items.map((item, i) => (
                <div key={i} data-priority-item className="flex self-stretch shrink-0">
                    {props.renderItem(item, i)}
                </div>
            ))}
        </nav>
    );
}
